import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, computed, effect, inject, input, signal, untracked, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';

import {
  FulfillmentState,
  GuaranteeDecision,
  GuaranteeProcessingState,
  GuaranteesRangePreset,
  GuaranteesViewMode,
  PrescriptionLineView,
  ServiceKind,
  clearGuaranteesFilters,
  clearIcd10Prefixes,
  initGuarantees,
  navigateBackGuarantee,
  presetWindow,
  resolveGuarantee,
  resolvePrescription,
  selectBreachCount,
  selectBreachRows,
  selectCanNavigateBack,
  selectCaseViews,
  selectDecisionQueue,
  selectDecisionQueueCount,
  selectDeliveryGapCount,
  selectDeliveryGaps,
  selectFacetCounts,
  selectFilteredVisitCount,
  selectFilteredVisits,
  selectGuaranteesError,
  selectGuaranteesFilters,
  selectGuaranteesLoaded,
  selectGuaranteesLoading,
  selectGuaranteesRangePreset,
  selectGuaranteesResolveError,
  selectGuaranteesResolving,
  selectGuaranteesSaveError,
  selectGuaranteesViewMode,
  selectIcd10Facets,
  selectNavigationBackTarget,
  selectPrescriptionRowCount,
  selectPrescriptionRows,
  selectSelectedGuaranteeId,
  selectShowDeleted,
  selectTimelineVisits,
  selectVisitCount,
  setGuaranteesSearch,
  setGuaranteesViewMode,
  setGuaranteesWindow,
  setIcd10Cursor,
  toggleDecisionFacet,
  toggleFulfillmentFacet,
  toggleIcd10Prefix,
  toggleKindFacet,
  toggleShowDeleted,
  toggleStatusFacet,
  widerPreset,
} from '@lib/guarantees/data-access';
import { selectLinesByGuaranteeId } from '@lib/guarantees/data-access/store/guarantees-v2.selectors';
import { BreachListComponent } from '@lib/guarantees/feature/components/breach-list/breach-list.component';
import { CareStreamComponent } from '@lib/guarantees/feature/components/care-stream/care-stream.component';
import { CasePanelComponent } from '@lib/guarantees/feature/components/case-panel/case-panel.component';
import { RANGE_PRESET_LABEL_KEY } from '@lib/guarantees/feature/components/common/guarantee-vocabulary';
import { allowsExecution, bookableSiblings } from '@lib/guarantees/feature/components/common/prescription-fulfillment';
import { SpinnerComponent } from '@lib/guarantees/feature/components/common/spinner.component';
import { DecisionQueueComponent } from '@lib/guarantees/feature/components/decision-queue/decision-queue.component';
import { DeliveryGapsComponent } from '@lib/guarantees/feature/components/delivery-gaps/delivery-gaps.component';
import { GuaranteeDetailsComponent } from '@lib/guarantees/feature/components/guarantee-details/guarantee-details.component';
import { GuaranteesSidePanel, GuaranteesToolbarComponent } from '@lib/guarantees/feature/components/guarantees-toolbar/guarantees-toolbar.component';
import { PrescriptionDetailsComponent } from '@lib/guarantees/feature/components/prescription-details/prescription-details.component';
import { PrescriptionTableComponent } from '@lib/guarantees/feature/components/prescription-table/prescription-table.component';
import { openExecutionForm, planningActionFor } from '@lib/guarantees/feature/operations/execution-form.component';
import { selectMemberProfile } from '@lib/members/data-access';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';

import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltip, MatTooltipDefaultOptions } from '@angular/material/tooltip';

import { MemberProfile } from '@lib/core/models/member.interface';

const PANEL_TITLE_KEYS: Record<GuaranteesSidePanel, string> = {
  cases: 'guaranteeV2.label.cases',
  breaches: 'guaranteeV2.label.breaches',
  guarantee: 'guaranteeV2.action.guaranteeDetails',
  prescription: 'guaranteeV2.action.prescriptionDetails',
};

const PANEL_HINT_KEYS: Record<GuaranteesSidePanel, string> = {
  cases: 'guaranteeV2.message.casesHint',
  breaches: 'guaranteeV2.message.breachesHint',
  guarantee: 'guaranteeV2.message.guaranteeDetailsHint',
  prescription: 'guaranteeV2.message.prescriptionDetailsHint',
};

const PANEL_ICONS: Record<GuaranteesSidePanel, string> = {
  cases: 'folder_managed',
  breaches: 'rule',
  guarantee: 'info',
  prescription: 'receipt_long',
};

/**
 * The member's care history: every guarantee in the chosen window, with the diagnoses it states
 * and how many of its prescriptions still await a ruling. The toolbar narrows what is on screen;
 * chain links move through it.
 *
 * Anything a row does not state is read beside the stream rather than inside it: a card and a line
 * each open a panel holding the whole of their record, so the stream stays scannable.
 */
@Component({
  selector: 'guarantees-member-guarantees-v2',
  standalone: true,
  imports: [
    DatePipe,
    MatIcon,
    MatTooltip,
    TranslatePipe,
    BreachListComponent,
    SpinnerComponent,
    CasePanelComponent,
    CareStreamComponent,
    DecisionQueueComponent,
    DeliveryGapsComponent,
    GuaranteeDetailsComponent,
    GuaranteesToolbarComponent,
    PrescriptionDetailsComponent,
    PrescriptionTableComponent,
  ],
  templateUrl: './member-guarantees-v2.component.html',
  /*
   * A tooltip is an overlay, and an overlay pane takes pointer events by default, so a visible
   * tooltip swallows every click landing on what it covers. This tab leans on tooltips inside
   * menus, where one opens directly over the next item and leaves it unclickable.
   *
   * Scoped to this tab rather than the portal: the element injector carries it to every tooltip
   * rendered beneath this page — menu panels included, since their content keeps the injector of
   * the template that declared it — and to nothing outside. The delays are Material's own; only
   * the interactivity, which a hint has no use for, is given up.
   */
  providers: [
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: { showDelay: 0, hideDelay: 0, touchendHideDelay: 1500, disableTooltipInteractivity: true } satisfies MatTooltipDefaultOptions,
    },
  ],
  host: { class: 'block h-full' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MemberGuaranteesV2Component {
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);

  /**
   * Withholds every control that writes. The record is the same one either way — a portal that may
   * read a member's care but not change it gets the whole surface minus its verbs, rather than a
   * second, thinner page that would drift from this one.
   */
  readOnly = input<boolean>(false);

  /** What a task or chat link asked to open, watched so a second link into the open tab still lands. */
  private readonly queryParams = toSignal(inject(ActivatedRoute).queryParamMap);

  protected readonly skeletonRows = [0, 1, 2, 3, 4];

  protected readonly panelTitleKeys = PANEL_TITLE_KEYS;
  protected readonly panelHintKeys = PANEL_HINT_KEYS;
  protected readonly panelIcons = PANEL_ICONS;

  /** One panel at a time: two would leave the timeline they qualify with nothing to read. */
  protected readonly sidePanel = signal<GuaranteesSidePanel | null>(null);

  /** The case the panel was opened onto from the stream, so it arrives on the row that was asked for. */
  protected readonly focusedCaseId = signal<string | null>(null);

  /** The guarantee whose record the details panel is showing. */
  private readonly detailsGuaranteeId = signal<string | null>(null);

  /** The prescription whose details the panel is showing. */
  private readonly detailsPrescriptionId = signal<string | null>(null);

  private readonly stickyHeader = viewChild<ElementRef<HTMLElement>>('stickyHeader');

  /** How tall the sticky toolbar is — where the side panel stops, and what it is measured against. */
  protected readonly headerHeight = signal(0);

  protected readonly visits = this.store.selectSignal(selectFilteredVisits);
  protected readonly loading = this.store.selectSignal(selectGuaranteesLoading);
  protected readonly loaded = this.store.selectSignal(selectGuaranteesLoaded);
  protected readonly error = this.store.selectSignal(selectGuaranteesError);

  protected readonly filters = this.store.selectSignal(selectGuaranteesFilters);
  protected readonly facets = this.store.selectSignal(selectFacetCounts);
  protected readonly icd10Facets = this.store.selectSignal(selectIcd10Facets);
  protected readonly rangePreset = this.store.selectSignal(selectGuaranteesRangePreset);
  protected readonly viewMode = this.store.selectSignal(selectGuaranteesViewMode);

  /** The same window the stream is showing, pivoted. Neither refetches, and both obey the facets. */
  protected readonly decisionQueue = this.store.selectSignal(selectDecisionQueue);
  protected readonly decisionQueueCount = this.store.selectSignal(selectDecisionQueueCount);
  protected readonly deliveryGaps = this.store.selectSignal(selectDeliveryGaps);
  protected readonly deliveryGapCount = this.store.selectSignal(selectDeliveryGapCount);
  protected readonly prescriptionRows = this.store.selectSignal(selectPrescriptionRows);
  protected readonly prescriptionRowCount = this.store.selectSignal(selectPrescriptionRowCount);
  protected readonly visitCount = this.store.selectSignal(selectFilteredVisitCount);
  protected readonly totalVisitCount = this.store.selectSignal(selectVisitCount);
  protected readonly breachCount = this.store.selectSignal(selectBreachCount);

  /** Integrity findings across the loaded window, listed so they can be acted on, not just counted. */
  protected readonly breachRows = this.store.selectSignal(selectBreachRows);

  /** Cases are the member's, not the window's, so they stand outside the timeline's filters. */
  protected readonly caseViews = this.store.selectSignal(selectCaseViews);

  /**
   * Every case the member has, closed ones included: the button says how much is behind it, and the
   * panel opens on closed cases when there is nothing else, so the two always agree.
   */
  protected readonly caseCount = computed(() => this.caseViews().length);

  /** Unfiltered on purpose: a facet set after the panel opened must not empty it. */
  private readonly timelineVisits = this.store.selectSignal(selectTimelineVisits);

  /** Every line of a record keyed by the record — what a booking reads for the sibling lines. */
  private readonly linesByGuarantee = this.store.selectSignal(selectLinesByGuaranteeId);

  /**
   * A line a caller asked to book, held until it is on screen to be booked. A deep link names a
   * prescription that may sit outside the loaded window, so the arrangement cannot open on the
   * click that sent it — the record has to be fetched and folded into the timeline first.
   */
  private readonly pendingBooking = signal<string | null>(null);

  protected readonly detailsVisit = computed(() => this.timelineVisits().find(visit => visit.guaranteeId === this.detailsGuaranteeId()));

  /** A line found across the loaded window, wherever its visit and diagnosis group hold it. */
  private lineById(prescriptionId: string): PrescriptionLineView | undefined {
    return this.timelineVisits()
      .flatMap(visit => visit.diagnosisGroups)
      .flatMap(group => group.lines)
      .find(line => line.prescription.id === prescriptionId);
  }

  /** The line the panel is showing, found across the window rather than passed up with the request. */
  protected readonly detailsLine = computed(() => {
    const prescriptionId = this.detailsPrescriptionId();

    return prescriptionId ? this.lineById(prescriptionId) : undefined;
  });

  protected readonly focusedGuaranteeId = this.store.selectSignal(selectSelectedGuaranteeId);
  protected readonly canNavigateBack = this.store.selectSignal(selectCanNavigateBack);
  protected readonly backTarget = this.store.selectSignal(selectNavigationBackTarget);
  protected readonly resolving = this.store.selectSignal(selectGuaranteesResolving);
  protected readonly resolveError = this.store.selectSignal(selectGuaranteesResolveError);

  /**
   * The last write that did not land. Kept apart from `error`: the timeline on screen is still
   * good, and only the change the health manager just made was lost — which they have to be told,
   * because the row reverts to its old value with nothing else to say why.
   */
  protected readonly saveError = this.store.selectSignal(selectGuaranteesSaveError);

  protected readonly showDeleted = this.store.selectSignal(selectShowDeleted);

  private readonly memberProfile = this.store.selectSignal<MemberProfile>(selectMemberProfile);
  /** The gateway takes the member's profile id and resolves the user id behind it. */
  private readonly memberId = computed(() => this.memberProfile()?.id);
  /** The other half of the same identity: what the hub names a member by when it announces a change. */
  private readonly memberUserId = computed(() => this.memberProfile()?.userId ?? null);

  /** The window itself came back empty — distinct from filters hiding everything in it. */
  protected readonly isEmpty = computed(() => this.loaded() && !this.loading() && this.totalVisitCount() === 0);

  /** An empty window is more often the wrong window than a member with no care. */
  protected readonly widerRange = computed(() => widerPreset(this.rangePreset()));

  protected rangeLabelKey(preset: GuaranteesRangePreset): string {
    return RANGE_PRESET_LABEL_KEY[preset];
  }

  protected readonly noResults = computed(() => this.loaded() && !this.loading() && this.totalVisitCount() > 0 && this.visitCount() === 0);

  constructor() {
    // Measured rather than assumed: the toolbar takes a second row as soon as something is
    // filtered, and the panel beside it has to follow that without a magic number.
    effect(onCleanup => {
      const element = this.stickyHeader()?.nativeElement;
      if (!element) {
        return;
      }

      const observer = new ResizeObserver(([entry]) => this.headerHeight.set(entry.target.getBoundingClientRect().height));
      observer.observe(element);
      onCleanup(() => observer.disconnect());
    });

    // The profile resolves after the tab opens, and switching member re-runs this, so the store
    // is always told whose timeline it holds rather than the component guessing when to fetch.
    effect(() => {
      const memberId = this.memberId();
      if (!memberId) {
        return;
      }

      // Read untracked: picking a preset is its own reload, not a re-init of the member.
      const preset = untracked(() => this.rangePreset());
      const range = presetWindow(preset);
      this.store.dispatch(initGuarantees({ memberId, memberUserId: untracked(() => this.memberUserId()), from: range.from, to: range.to, preset }));
    });

    // Deep links: tasks and chat land here naming a record (`?guarantee=`) or a line
    // (`?prescription=`). Declared after the init effect so the member is already told to the
    // store when the resolve asks it to find the target — which may sit outside the loaded window.
    effect(() => {
      const memberId = this.memberId();
      const params = this.queryParams();
      if (!memberId || !params) {
        return;
      }

      const guaranteeId = params.get('guarantee');
      const prescriptionId = params.get('prescription');
      // Chat sends the manager here to arrange delivery, not merely to look at the line.
      const booking = params.get('action') === 'book' && !this.readOnly() ? prescriptionId : null;
      untracked(() => {
        if (guaranteeId) {
          this.store.dispatch(resolveGuarantee({ guaranteeId }));
        } else if (prescriptionId) {
          this.store.dispatch(resolvePrescription({ prescriptionId, track: true }));
          this.pendingBooking.set(booking);
        }
      });
    });

    // The other half of the deep link above: the line asked for arrives with the window or with the
    // chain fetched for it, and the arrangement opens the moment it is there. A resolve that failed
    // clears the request rather than leaving it waiting for a line that is never coming.
    effect(() => {
      const prescriptionId = this.pendingBooking();
      if (!prescriptionId) {
        return;
      }

      if (this.resolveError()) {
        untracked(() => this.pendingBooking.set(null));

        return;
      }

      const line = this.lineById(prescriptionId);
      if (!line) {
        return;
      }

      untracked(() => {
        this.pendingBooking.set(null);
        this.arrange(line);
      });
    });
  }

  /**
   * Arranging delivery of a line reached by link. The same question the stream asks — the other
   * lines one arrangement could also cover are offered with it — so a booking made from chat is the
   * booking made from the row, and not a thinner second version of it.
   */
  private arrange(line: PrescriptionLineView): void {
    const memberId = this.memberId();
    if (!memberId || !allowsExecution(line)) {
      return;
    }

    const guaranteeId = line.prescription.guaranteeId;
    const siblings = this.linesByGuarantee().get(guaranteeId) ?? [];

    openExecutionForm(this.dialog, {
      memberId,
      line,
      siblings: bookableSiblings(siblings, line),
    }).subscribe(result => {
      if (result) {
        this.store.dispatch(planningActionFor(guaranteeId, result));
      }
    });
  }

  protected selectPreset(preset: GuaranteesRangePreset): void {
    const range = presetWindow(preset);
    this.store.dispatch(setGuaranteesWindow({ from: range.from, to: range.to, preset }));
  }

  /** A re-pivot of what is already loaded, so nothing is fetched and no facet is disturbed. */
  protected selectViewMode(mode: GuaranteesViewMode): void {
    this.store.dispatch(setGuaranteesViewMode({ mode }));
  }

  protected toggleDeleted(): void {
    this.store.dispatch(toggleShowDeleted());
  }

  /** Pressing the button of the panel already open closes it, so one control does both. */
  protected togglePanel(panel: GuaranteesSidePanel): void {
    this.sidePanel.update(current => (current === panel ? null : panel));
  }

  protected closePanel(): void {
    this.sidePanel.set(null);
    this.focusedCaseId.set(null);
    this.detailsGuaranteeId.set(null);
    this.detailsPrescriptionId.set(null);
  }

  /** The rest of a card's record. Read from the window rather than copied, so a write shows here. */
  protected openGuaranteeDetails(guaranteeId: string): void {
    this.detailsGuaranteeId.set(guaranteeId);
    this.sidePanel.set('guarantee');
  }

  /** Everything behind one prescription's rows, beside the stream rather than inside it. */
  protected openPrescriptionDetails(prescriptionId: string): void {
    this.detailsPrescriptionId.set(prescriptionId);
    this.sidePanel.set('prescription');
  }

  /** Reading the case a diagnosis was filed under, without leaving the stream to look for it. */
  protected openCase(caseId: string): void {
    this.focusedCaseId.set(caseId);
    this.sidePanel.set('cases');
  }

  /**
   * A chain link may point outside the loaded window, so the store is asked to find its target.
   *
   * Opening a visit also returns to the stream. Selecting one is only visible there, so a work
   * list's own link would otherwise look inert: it would move the selection under a view that
   * does not render it. The dispatch is a no-op when the stream is already what is on screen.
   */
  protected openGuarantee(guaranteeId: string): void {
    this.store.dispatch(setGuaranteesViewMode({ mode: 'timeline' }));
    this.store.dispatch(resolveGuarantee({ guaranteeId, track: true }));
  }

  /** Stepping back lands on a visit, so it returns to the view that shows one. */
  protected navigateBack(): void {
    this.store.dispatch(setGuaranteesViewMode({ mode: 'timeline' }));
    this.store.dispatch(navigateBackGuarantee());
  }

  protected search(value: string): void {
    this.store.dispatch(setGuaranteesSearch({ search: value }));
  }

  protected toggleDecision(decision: GuaranteeDecision): void {
    this.store.dispatch(toggleDecisionFacet({ decision }));
  }

  protected toggleKind(kind: ServiceKind): void {
    this.store.dispatch(toggleKindFacet({ kind }));
  }

  protected toggleFulfillment(fulfillment: FulfillmentState): void {
    this.store.dispatch(toggleFulfillmentFacet({ fulfillment }));
  }

  protected toggleStatus(status: GuaranteeProcessingState): void {
    this.store.dispatch(toggleStatusFacet({ status }));
  }

  protected toggleIcd10(prefix: string): void {
    this.store.dispatch(toggleIcd10Prefix({ prefix }));
  }

  /** Browsing the tree is not filtering, so this only moves the picker. */
  protected moveIcd10Cursor(cursor: string | null): void {
    this.store.dispatch(setIcd10Cursor({ cursor }));
  }

  protected clearIcd10(): void {
    this.store.dispatch(clearIcd10Prefixes());
  }

  protected clearFilters(): void {
    this.store.dispatch(clearGuaranteesFilters());
  }

  protected reload(): void {
    this.selectPreset(this.rangePreset());
  }
}
